import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { getApiBase } from "../api";

// ============================================================================
// CARRIER LOAD BOARD — Mobile-Optimized
// Sticky search, horizontal scroll filter chips, bottom-sheet bid modal
// ============================================================================

// Safely extract city/state from potentially object fields
function safeCity(city, addr) {
  if (city && typeof city === "string") return city;
  if (addr && typeof addr === "object") return addr.city || "?";
  return "?";
}
function safeState(state, addr) {
  if (state && typeof state === "string") return state;
  if (addr && typeof addr === "object") return addr.state || "";
  return "";
}

const EQUIPMENT_FILTERS = [
  { key: "ALL",       label: "All" },
  { key: "DRY_VAN",   label: "Dry Van" },
  { key: "REEFER",    label: "Reefer" },
  { key: "FLATBED",   label: "Flatbed" },
  { key: "TANKER",    label: "Tanker" },
  { key: "STEP_DECK", label: "Step Deck" },
  { key: "HOTSHOT",   label: "Hotshot" },
];

// ── Bottom-sheet Bid Modal ──
function BidModal({ load, onClose, onSubmit }) {
  const [bidAmount, setBidAmount] = useState(
    load.rateAmountCents ? (load.rateAmountCents / 100).toString() : ""
  );
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!bidAmount) return;
    setSubmitting(true);
    await onSubmit(load.id, parseFloat(bidAmount), notes);
    setSubmitting(false);
  }

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }} />
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1001,
        background: "#1e293b", borderRadius: "20px 20px 0 0",
        padding: "20px 16px", paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.6)",
        animation: "carrier-slideUp 0.25s cubic-bezier(0.32,0.72,0,1)",
        maxHeight: "80vh", maxHeight: "80dvh", overflowY: "auto",
      }}>
        {/* Drag handle */}
        <div style={{ width: 40, height: 4, background: "#475569", borderRadius: 2, margin: "0 auto 16px" }} />

        <div style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", marginBottom: 4 }}>Place Bid</div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          {load.referenceNumber} — {safeCity(load.originCity, load.originAddress)}, {safeState(load.originState, load.originAddress)} → {safeCity(load.destCity, load.destAddress)}, {safeState(load.destState, load.destAddress)}
        </div>

        {load.rateAmountCents > 0 && (
          <div style={{
            padding: "10px 14px", borderRadius: 10, marginBottom: 14,
            background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)",
            fontSize: 13, color: "#86efac",
          }}>
            Posted rate: <strong>${(load.rateAmountCents / 100).toLocaleString()}</strong>
          </div>
        )}

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
          Your Bid ($)
        </label>
        <input
          type="number" inputMode="decimal" value={bidAmount}
          onChange={e => setBidAmount(e.target.value)}
          placeholder="0.00" autoFocus
          style={{
            width: "100%", padding: "14px 16px", borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.3)",
            color: "#f1f5f9", fontSize: 20, fontWeight: 700,
            boxSizing: "border-box", outline: "none",
          }}
        />

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 6, marginTop: 14 }}>
          Notes (optional)
        </label>
        <textarea
          value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Equipment details, availability..."
          rows={2}
          style={{
            width: "100%", padding: "12px 16px", borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.3)",
            color: "#f1f5f9", fontSize: 14, boxSizing: "border-box", outline: "none",
            resize: "none", fontFamily: "inherit",
          }}
        />

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "14px", borderRadius: 12, cursor: "pointer",
            border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
            color: "#94a3b8", fontSize: 15, fontWeight: 600,
            WebkitTapHighlightColor: "transparent",
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!bidAmount || submitting} style={{
            flex: 2, padding: "14px", borderRadius: 12, border: "none", cursor: "pointer",
            background: bidAmount && !submitting ? "#22c55e" : "#334155",
            color: bidAmount && !submitting ? "white" : "#64748b",
            fontSize: 15, fontWeight: 700,
            WebkitTapHighlightColor: "transparent",
          }}>
            {submitting ? "Submitting..." : `Bid $${bidAmount || "0"}`}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Board Card ──
function BoardCard({ load, onTap, onBid }) {
  const rate = load.rateAmountCents ? `$${(load.rateAmountCents / 100).toLocaleString()}` : "Open";
  const rpm = load.rateAmountCents && load.distanceMiles
    ? `$${(load.rateAmountCents / 100 / load.distanceMiles).toFixed(2)}/mi` : "";

  return (
    <div style={{
      background: "#1e293b", borderRadius: 14, overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.06)",
      marginBottom: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
    }}>
      <div onClick={onTap} style={{ padding: "12px 14px", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
        {/* Top: equip badge + rate */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <span style={{
              display: "inline-block", padding: "3px 8px", borderRadius: 6,
              background: "rgba(139,92,246,0.12)", color: "#a78bfa",
              fontSize: 11, fontWeight: 700,
            }}>
              {load.equipmentType || "Van"}
            </span>
            {load.referenceNumber && (
              <div style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 800, marginTop: 3 }}>{load.referenceNumber}</div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 19, fontWeight: 900, color: "#22c55e" }}>{rate}</div>
            {rpm && <div style={{ fontSize: 11, color: "#64748b" }}>{rpm}</div>}
          </div>
        </div>

        {/* Route compact */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12 }}>📍</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>
            {safeCity(load.originCity, load.originAddress)}, {safeState(load.originState, load.originAddress)}
          </span>
          <span style={{ color: "#475569", fontSize: 13 }}>→</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>
            {safeCity(load.destCity, load.destAddress)}, {safeState(load.destState, load.destAddress)}
          </span>
        </div>

        {/* Details */}
        <div style={{ display: "flex", gap: 10, fontSize: 12, color: "#64748b", flexWrap: "wrap" }}>
          {load.distanceMiles && <span>📏 {load.distanceMiles} mi</span>}
          {load.weightLbs && <span>⚖️ {load.weightLbs.toLocaleString()} lbs</span>}
          {load.pickupStart && (
            <span>📅 {new Date(load.pickupStart).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          )}
          {load.bidCount > 0 && <span>🏷️ {load.bidCount} bid{load.bidCount !== 1 ? "s" : ""}</span>}
        </div>
      </div>

      {/* Bid button / status */}
      {load.myBidCents ? (
        <div style={{
          width: "100%", padding: "12px", boxSizing: "border-box",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(34,197,94,0.08)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: "#86efac", fontSize: 14, fontWeight: 700 }}>
            ✅ Your Bid: ${(load.myBidCents / 100).toLocaleString()}
          </span>
          <button onClick={(e) => { e.stopPropagation(); onBid(); }} style={{
            padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(34,197,94,0.3)",
            background: "rgba(34,197,94,0.1)", color: "#86efac",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}>
            Update
          </button>
        </div>
      ) : (
        <button onClick={(e) => { e.stopPropagation(); onBid(); }} style={{
          width: "100%", padding: "12px", border: "none", cursor: "pointer",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(239,68,68,0.05)", color: "#ef4444",
          fontSize: 14, fontWeight: 700,
          WebkitTapHighlightColor: "transparent",
        }}>
          💰 Place Bid
        </button>
      )}
    </div>
  );
}

export default function CarrierLoadBoard() {
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const apiBase = useMemo(() => getApiBase(), []);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [equipFilter, setEquipFilter] = useState("ALL");
  const [bidLoad, setBidLoad] = useState(null);
  const [bidMsg, setBidMsg] = useState("");
  const [backhaulCount, setBackhaulCount] = useState(0);

  // Check for backhaul matches
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/carrier/backhaul-matches`, {
          headers: { "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        });
        const data = await res.json();
        setBackhaulCount((data.matches || []).length);
      } catch (e) { /* silent */ }
    })();
  }, [apiBase, carrierEmail]);

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/carrier/loads/board`, {
        headers: { "x-carrier-email": carrierEmail || "tim@symplecode.com" },
      });
      const data = await res.json();
      setLoads(data.loads || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiBase, carrierEmail]);

  useEffect(() => { fetchLoads(); }, [fetchLoads]);

  const submitBid = async (loadId, amount, notes) => {
    try {
      const res = await fetch(`${apiBase}/carrier/loads/${loadId}/bid`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        body: JSON.stringify({ bidAmountCents: Math.round(amount * 100), notes }),
      });
      if (res.ok) {
        setBidMsg("✅ Bid submitted!"); setBidLoad(null);
        fetchLoads(); // refresh to show bid status
        setTimeout(() => setBidMsg(""), 3000);
      } else {
        const d = await res.json();
        setBidMsg(`❌ ${d.detail || "Bid failed"}`);
      }
    } catch (e) { setBidMsg(`❌ ${e.message}`); }
  };

  const filtered = useMemo(() => {
    let result = loads;
    if (equipFilter !== "ALL") {
      result = result.filter(l => (l.equipmentType || "").toUpperCase().replace(/[\s-]/g, "_").includes(equipFilter));
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(l => {
        const oc = safeCity(l.originCity, l.originAddress);
        const os = safeState(l.originState, l.originAddress);
        const dc = safeCity(l.destCity, l.destAddress);
        const ds = safeState(l.destState, l.destAddress);
        return [oc, os, dc, ds, l.referenceNumber]
          .some(v => (v || "").toLowerCase().includes(s));
      });
    }
    return result;
  }, [loads, equipFilter, search]);

  return (
    <div>
      {/* Sticky header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 60,
        background: "linear-gradient(180deg, #0f172a 0%, #0f172aee 100%)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        padding: "14px 14px 0",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#f1f5f9", letterSpacing: -0.5 }}>Load Board</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{loads.length} loads available</div>
          </div>
          <button onClick={fetchLoads} style={{
            width: 38, height: 38, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.04)", color: "#94a3b8", fontSize: 16,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            WebkitTapHighlightColor: "transparent",
          }}>🔄</button>
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 10 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15 }}>🔍</span>
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search city, state, or load #"
            style={{
              width: "100%", padding: "12px 12px 12px 38px", borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)",
              color: "#f1f5f9", fontSize: 15, boxSizing: "border-box", outline: "none",
            }}
          />
        </div>

        {/* Equipment filter chips — horizontal scroll */}
        <div style={{
          display: "flex", gap: 6, paddingBottom: 10,
          overflowX: "auto", WebkitOverflowScrolling: "touch",
          msOverflowStyle: "none", scrollbarWidth: "none",
        }}>
          {EQUIPMENT_FILTERS.map(f => (
            <button key={f.key} onClick={() => setEquipFilter(f.key)} style={{
              padding: "8px 14px", borderRadius: 20, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
              background: equipFilter === f.key ? "#ef4444" : "rgba(255,255,255,0.06)",
              color: equipFilter === f.key ? "white" : "#94a3b8",
              WebkitTapHighlightColor: "transparent",
            }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Toast */}
      {bidMsg && (
        <div style={{
          margin: "8px 12px", padding: "10px 14px", borderRadius: 10,
          background: bidMsg.includes("❌") ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
          color: bidMsg.includes("❌") ? "#fca5a5" : "#86efac",
          fontSize: 13, fontWeight: 600,
        }}>{bidMsg}</div>
      )}

      {/* Backhaul Alert Banner */}
      {backhaulCount > 0 && (
        <div onClick={() => navigate('/app/carrier/backhaul')} style={{
          margin: '0 14px 10px', padding: '12px 14px', borderRadius: 12,
          background: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(16,185,129,0.08) 100%)',
          border: '1px solid rgba(34,197,94,0.25)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
          WebkitTapHighlightColor: 'transparent',
        }}>
          <span style={{ fontSize: 24 }}>🤖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#22c55e' }}>
              {backhaulCount} Backhaul Match{backhaulCount !== 1 ? 'es' : ''} Found!
            </div>
            <div style={{ fontSize: 12, color: '#86efac' }}>
              Loads near your delivery points heading home. Tap to view →
            </div>
          </div>
          <span style={{
            background: '#22c55e', color: '#0f172a', padding: '4px 10px',
            borderRadius: 999, fontSize: 13, fontWeight: 900,
          }}>{backhaulCount}</span>
        </div>
      )}
      {/* Cards */}
      <div style={{ padding: "6px 10px 20px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            Loading available loads...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#fca5a5", background: "rgba(239,68,68,0.08)", borderRadius: 16 }}>
            <div style={{ marginBottom: 12 }}>{error}</div>
            <button onClick={fetchLoads} style={{
              padding: "10px 20px", borderRadius: 10, border: "1px solid #ef4444",
              background: "transparent", color: "#ef4444", fontWeight: 700, cursor: "pointer",
            }}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>🔍</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#94a3b8" }}>No loads found</div>
            <div style={{ fontSize: 13 }}>Try adjusting your filters</div>
          </div>
        ) : (
          filtered.map(load => (
            <BoardCard
              key={load.id} load={load}
              onTap={() => navigate(`/app/carrier/loads/${load.id}`)}
              onBid={() => setBidLoad(load)}
            />
          ))
        )}
      </div>

      {bidLoad && <BidModal load={bidLoad} onClose={() => setBidLoad(null)} onSubmit={submitBid} />}
    </div>
  );
}
